// Generic collapsible display for item/description pairs

import React, { useState } from 'react';
import { HatGlasses } from 'lucide-react';
import PlayerStyledButton from '@/components/games/PlayerStyledButton';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldTitle,
} from '@/components/ui/field';
import {
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useRoomMyPlayer } from '../../hooks';

export interface AssignmentItem {
  item: string;
  description: string;
}

export interface SecretAssignmentProps {
  items: AssignmentItem[];
  expanded?: boolean;
  defaultExpanded?: boolean;
  onExpandedChange?: (isExpanded: boolean) => void;
}

const SecretAssignment: React.FC<SecretAssignmentProps> = ({
  items,
  expanded,
  defaultExpanded = false,
  onExpandedChange,
}) => {
  const isControlled = expanded !== undefined;
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isExpanded = isControlled ? expanded : internalExpanded;
  const myPlayer = useRoomMyPlayer();

  const handleOpenChange = (next: boolean) => {
    if (!isControlled) {
      setInternalExpanded(next);
    }
    onExpandedChange?.(next);
  };

  return (
    <div className="fixed right-4 top-4 z-[450]">
      {isExpanded ? (
        <button
          type="button"
          className="fixed inset-0 z-[440] bg-neutral-950/65"
          aria-label="Close secret assignment"
          onClick={() => handleOpenChange(false)}
        />
      ) : null}
      <Popover open={isExpanded} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <PlayerStyledButton
            active={isExpanded}
            className="relative z-[470]"
            aria-pressed={isExpanded}
            aria-label="Open secret assignment"
          >
            <HatGlasses />
          </PlayerStyledButton>
        </PopoverTrigger>
        <PopoverContent align="end" className="z-[480] w-80 p-3">
          <PopoverHeader>
            <PopoverTitle>
              {myPlayer?.playerName
                ? `Assignment for ${myPlayer.playerName}`
                : 'Your Assignment'}
            </PopoverTitle>
          </PopoverHeader>

          <FieldGroup>
            {items.map((assignmentItem, index) => (
              <Field key={index} className="rounded-md border bg-card p-3">
                <FieldContent>
                  <FieldTitle>{assignmentItem.item}</FieldTitle>
                  <FieldDescription>
                    {assignmentItem.description}
                  </FieldDescription>
                </FieldContent>
              </Field>
            ))}
          </FieldGroup>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default SecretAssignment;
